import React from 'react';
import { motion } from 'framer-motion';
import { Database, Clock, RefreshCw, AlertTriangle, CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function BackupStatus({
  lastBackup,
  nextBackup,
  status = 'success',
  progress = 0,
  onBackupNow,
  loading = false,
}) {
  const statusConfig = {
    success: { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-900/20', label: 'Last backup successful' },
    failed: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-900/20', label: 'Backup failed' },
    running: { icon: Loader2, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20', label: 'Backup in progress...' },
  };

  const config = statusConfig[status] || statusConfig.success;
  const StatusIcon = config.icon;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${config.bg}`}>
            <Database size={22} className={config.color} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Database Backup</h3>
            <p className={`text-xs font-medium ${config.color}`}>{config.label}</p>
          </div>
        </div>
        <div className="relative">
          {status === 'running' ? (
            <motion.span
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="flex w-3 h-3"
            >
              <span className="absolute inline-flex w-full h-full rounded-full bg-blue-400 opacity-75" />
              <span className="relative inline-flex w-3 h-3 rounded-full bg-blue-500" />
            </motion.span>
          ) : status === 'success' ? (
            <span className="flex w-3 h-3 rounded-full bg-emerald-500" />
          ) : (
            <span className="flex w-3 h-3 rounded-full bg-red-500" />
          )}
        </div>
      </div>

      {status === 'running' && progress > 0 && (
        <div className="mb-4">
          <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
              className="h-full bg-blue-500 rounded-full"
            />
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 text-right">{progress}%</p>
        </div>
      )}

      <div className="space-y-2.5 mb-5">
        {lastBackup && (
          <div className="flex items-center gap-2.5 text-sm">
            <Clock size={14} className="text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">Last backup:</span>
            <span className="text-gray-700 dark:text-gray-300 font-medium">
              {new Date(lastBackup).toLocaleString('en-PK', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
            </span>
          </div>
        )}
        {nextBackup && (
          <div className="flex items-center gap-2.5 text-sm">
            <Clock size={14} className="text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">Next backup:</span>
            <span className="text-gray-700 dark:text-gray-300 font-medium">
              {new Date(nextBackup).toLocaleString('en-PK', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
            </span>
          </div>
        )}
      </div>

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onBackupNow}
        disabled={loading || status === 'running'}
        className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading || status === 'running' ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <RefreshCw size={16} />
        )}
        {status === 'running' ? 'Backing up...' : 'Create Backup Now'}
      </motion.button>
    </div>
  );
}
